<template>
  <div
    ref="flashcard-view-container"
    class="flashcard-view-container"
  >
    <flashcard-header
      :answered="answered"
      :breadcrumbs="breadcrumbs"
      :category="currentFlashcardCategory"
      :flashcard="currentFlashcard"
      :sharing-enabled="sharingEnabled"
      :show-progress="true"
    />
    <flashcard
      :answered="answered"
      :answer-ids="answerIds"
      :category="currentFlashcardCategory"
      :correct="correct"
      :flashcard="currentFlashcard"
      :raw-answer-data="rawAnswerData"
      :sharing-enabled="sharingEnabled"
      :show-flashcard-metadata="showFlashcardMetadata"
      :terminology="isTerminology"
      @shift-enter="answerFlashcard"
      @set-answer-ids="setAnswerIds"
      @set-raw-answer-data="setRawAnswerData"
    />
    <rating
      v-if="showRating && !lockedRationales && answered"
      :asset="currentFlashcard"
      :rateable-id="currentFlashcard && currentFlashcard.id"
      :rateable-type="'V3Flashcard'"
    />
    <flashcard-footer
      :allow-skipping="!onTour"
      :answered="answered"
      :answer-ids="answerIds"
      :category="currentFlashcardCategory"
      :correct="correct"
      :flashcard="currentFlashcard"
      :raw-answer-data="rawAnswerData"
      :terminology="isTerminology"
      :sharing-enabled="sharingEnabled"
      :show-bookmark="!onTour"
      :show-flashcard-statuses="true"
      @answer="answerFlashcard"
      @skip="skipFlashcard"
      @tag="tagFlashcard"
    />
  </div>
</template>

<script>

import Flashcard from '@components/flashcards/flashcard';
import FlashcardFooter from '@components/flashcards/footer';
import FlashcardHeader from '@components/flashcards/header';
import FlashcardViewMixin from '@/mixins/flashcardViews';
import GlobalMixin from '@/mixins/global';
import RateLimitMixin from '@/mixins/rate-limit';
import Rating from '@components/singletons/rating';
import RatingClass from '@/classes/rating';
import QuestionsUIV2Mixin from '@/mixins/questions-ui-v2';

import { checkRationalesLocked, hasAdvancedStats } from '@/utils/flashcards';

export default {
  components: { Flashcard, FlashcardFooter, FlashcardHeader, Rating },
  mixins: [FlashcardViewMixin, GlobalMixin, RateLimitMixin, QuestionsUIV2Mixin],
  // this is designed to capture users who have userd the back button after starting the tour
  // so that we can reset the onTour variable and re-enable parts of the app we blur/disable during the tour
  beforeRouteLeave(to, from, next) {
    if (this.$store.getters['session/onTour']) {
      // the component at categories#complete will redirect to categories#index when a user is on the tour
      // so we need to make sure we don't reset the onTour variable when the user goes to that route
      if (to.name !== 'flashcard#show' && to.name !== 'categories#complete') {
        this.$store.commit('session/setOnTour', false);
        this.$store.commit('session/setTourType', '');
        this.$store.commit('navigationItems/resetDynamicNavigationItems');
      }
    }
    next();
  },
  computed: {
    breadcrumbs() {
      if (!this.currentFlashcardCategory) return [];
      let categories = this.currentFlashcardCategory.path.split('.').map(id => this.$store.getters['categories/categories'][id]);
      let crumbs = categories.map(c => {
        return { name: c.name, route: {name: 'categories#index', params: { categoryId: c.id}, query: this.currentQuery}};
      });
      return crumbs;
    },
    currentFlashcardCategory() {
      if (this.currentFlashcard?.id) {
        let categoryId = this.$route.params.categoryId;
        let categories = this.currentFlashcard.categoryIds.map(id => this.$store.getters['categories/categories'][id]).filter(c => c);

        if (categoryId) {
          if (categoryId === 'all') {
            return categories.find(c => c.categoryTypeId == this.currentResource.resourceId);
          } else {
            let category = categories.find(c => c.id == categoryId);

            if (!category) {
              // this happens if we are studying from a top level category and flashcard belongs to child catgory
              let parent = this.$store.getters['categories/categories'][categoryId];
              let retVal;

              parent.descendants.forEach(id => {
                if (this.currentFlashcard.categoryIds.includes(id)) {
                  retVal = this.$store.getters['categories/categories'][id];
                  return;
                }
              });

              return retVal;
            } else {
              return category;
            }
          }
        }
      }
    },
    lockedRationales() {
      if (this.currentFlashcardCategory) {
        return checkRationalesLocked(this.$store, this.currentFlashcardCategory, this.$apptimize);
      }
    },
    nextFlashcardRoute() {
      if (this.nextFlashcard) {
        return { name: 'categories#flashcard', params: { categoryId: this.$route.params.categoryId, flashcardId: this.nextFlashcard }, query: this.currentQuery };
      } else {
        return { name: 'categories#complete', params: { categoryId: this.$route.params.categoryId }, query: this.currentQuery };
      }
    },
    onTour() {
      return this.$store.getters['session/onTour'];
    },
    sharingEnabled() {
      const enabled = !!(this.$store.getters['productVars/get']['flashcardShareMessage']);
      return enabled && !this.onTour;
    },
    showFlashcardMetadata() {
      return hasAdvancedStats(this.$store, this.$apptimize, this.$analytics);
    },
    showRating() {
      return false && RatingClass.enabled(this.$store, 'flashcard');
    },
  },
  watch: {
    '$route.params.flashcardId': function(id) {
      if (this.$store.getters['session/onTour']) {
        this.$analytics.events.viewedOnboardingFlashcard(id);
      }
      if(!this.$store.getters['flashcards/flashcards'][id]) {
        let params = { ids: [id], rateLimited: this.rateLimitingEnabled };
        this.$store.dispatch('flashcards/fetchFlashcards', params);
      }
    },
    'currentFlashcard.id': {
      immediate: true,
      handler: function(id) {
        this.resetFlashcard(id);
      },
    },
  },
  created() {
    let stateParams = ['bookmarks', 'flashcards', 'stats'];
    this.$store.dispatch('flashcardStates/fetchStates', stateParams);
    let params = {};

    if (this.$route.params.categoryId === 'all') {
        params.categoryTypeId = this.currentResource.resourceId;
      } else if (this.$route.params.categoryId !== 'all') {
        params.categoryId = this.$route.params.categoryId;
      }

    if (this.$route.params.flashcardId) {
      params.flashcardId = this.$route.params.flashcardId;
    }

    const filter = this.$store.getters['flashcardStatuses/flashcardStatusFilter'];
    if (filter.name !== 'unanswered') {
      if (filter.name === 'bookmarked') {
        params.flashcardStatusId = 'bookmarked';
      } else {
        params.flashcardStatusId = filter.id;
      }
    }

    params.rateLimited = this.rateLimitingEnabled;

    this.sessionPromise = this.$store.dispatch('flashcards/createFlashcardSession', params);
  },
  mounted() {
    this.sessionPromise.then(flashcard => {
      if (flashcard) {
        const categoryId = this.$route.params.categoryId;

        this.$router.replace({
          name: this.getCorrectRouteName(flashcard.type),
          params: { categoryId: categoryId, flashcardId: flashcard.id },
          query: this.currentQuery,
        });

        this.$nextTick().then(() => {
          if (this.flashcardIsRateLimited(flashcard, this.currentFlashcardCategory) && this.creditLimitReached) {
            this.$modal.show('rate-limit-reached', {
              category: this.currentFlashcardCategory,
              flashcard: this.currentFlashcard,
            });
          }
        });
      } else {
        this.$router.push({ name: 'categories#index', params: { categoryId: this.$route.params.categoryId }, query: this.currentQuery });
      }
    }).catch(e => {
      this.$router.go(-1);
      Promise.reject(e);
    });
  },
};
</script>

<style lang='scss' scoped>
  .flashcard-view-container {
    @include flashcard-view;
  }
</style>
